import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import { useSelector, useDispatch } from 'react-redux';

import { updateClient } from 'src/module/authentication/action';
import { getClient } from 'src/module/authentication/selector';
import SwitchField from 'src/component/UI/SettingsModal/SettingsModalFields/SwitchField';
import { Map, List } from 'immutable';
import { InputLabel, Tooltip, IconButton } from '@mui/material';
import Button from 'src/component/UI/Button';
import HelpCircleIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/DeleteSharp';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'src/i18n';

export default function IpWhitelistSection () {
  const { t } = useTranslation();
  const client: any = useSelector(getClient);
  const dispatch = useDispatch();

  const existingWhitelist = client.get('ip_whitelist');
  const initialIpList: string[] = List.isList(existingWhitelist)
    ? existingWhitelist.toJS()
    : Array.isArray(existingWhitelist) ? existingWhitelist : [];
  const [ipWhitelistEnabled, setIpWhitelistEnabled] = useState(initialIpList.length > 0);
  const [ipEntries, setIpEntries] = useState<string[]>(initialIpList.length > 0 ? initialIpList : ['']);
  const [ipErrors, setIpErrors] = useState<string[]>([]);

  const isValidCIDR = (cidr: string): boolean => {
    const parts = cidr.trim().split('/');
    if (parts.length !== 2) return false;
    const ip = parts[0];
    const bits = parseInt(parts[1], 10);
    const octets = ip.split('.');
    if (octets.length !== 4) return false;
    if (isNaN(bits) || bits < 0 || bits > 32) return false;
    return octets.every(o => {
      const n = parseInt(o, 10);
      return !isNaN(n) && n >= 0 && n <= 255 && String(n) === o;
    });
  };

  const saveIpWhitelist = (entries: string[], enabled: boolean) => {
    if (!enabled) {
      dispatch(updateClient(client.get('name'), Map({ ipWhitelist: [] })));
      return;
    }
    const validEntries = entries.filter(e => e.trim() !== '');
    const errors = validEntries.map(e => isValidCIDR(e) ? '' : t('clientProperties.invalidCIDR'));
    setIpErrors(errors);
    if (errors.some(e => e !== '')) return;
    if (validEntries.length === 0) return;
    dispatch(updateClient(client.get('name'), Map({ ipWhitelist: validEntries.map(e => e.trim()) })));
  };

  return (
    <div className='ip-whitelist-section' style={{ padding: '0px 0px 8px 0px' }}>
      <InputLabel className='mfa-label'>
        {t('clientProperties.ipWhitelist')}
        <Tooltip title={t('clientProperties.ipWhitelistTooltip')} enterTouchDelay={0}>
          <HelpCircleIcon className='help-icon' style={{ fontSize: 16, marginLeft: 4, verticalAlign: 'middle' }} />
        </Tooltip>
      </InputLabel>

      <div className='mfa-switch'>
        <SwitchField
          value={ipWhitelistEnabled}
          field={Map({ label: t('clientProperties.ipWhitelistEnable'), id: null })}
          handleChange={() => {
            const newEnabled = !ipWhitelistEnabled;
            setIpWhitelistEnabled(newEnabled);
            if (!newEnabled) {
              setIpEntries(['']);
              setIpErrors([]);
              saveIpWhitelist([], false);
            }
          }}
        />
      </div>

      {ipWhitelistEnabled && (
        <div style={{ marginTop: '8px' }}>
          {ipEntries.map((entry, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="10.0.0.0/24"
                value={entry}
                onChange={(e) => {
                  const updated = [...ipEntries];
                  updated[index] = e.target.value;
                  setIpEntries(updated);
                  if (ipErrors[index]) {
                    const updatedErrors = [...ipErrors];
                    updatedErrors[index] = '';
                    setIpErrors(updatedErrors);
                  }
                }}
                error={Boolean(ipErrors[index])}
                helperText={ipErrors[index] || ''}
                variant='outlined'
                InputLabelProps={{ shrink: false }}
              />
              {ipEntries.length > 1 && (
                <IconButton
                  onClick={() => {
                    const updated = ipEntries.filter((_, i) => i !== index);
                    const updatedErrors = ipErrors.filter((_, i) => i !== index);
                    setIpEntries(updated);
                    setIpErrors(updatedErrors);
                  }}
                  aria-label={t('clientProperties.ipWhitelistRemove')}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', marginBottom: '16px', justifyContent: 'flex-end' }}>
            <Button
              icon={<AddIcon style={{ fontSize: 14 }} />}
              onClick={() => setIpEntries([...ipEntries, ''])}
            >
              {t('clientProperties.ipWhitelistAdd')}
            </Button>

            <Button
              cta
              onClick={() => saveIpWhitelist(ipEntries, true)}
            >
              {t('clientProperties.ipWhitelistSave')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
